(ns dre.widgets.top-terms.events
  (:require
   [clojure.string :as str]
   [day8.re-frame.tracing :refer-macros [fn-traced]]
   [dre.app.events :as app-events]
   [dre.coll :refer [distinct-by]]
   [dre.components.subject :as subj]
   [dre.components.widget.events :as widget-events]
   [dre.fx :as-alias dre.fx]
   [dre.i18n.utils :as i18n-utils]
   [dre.i18n.views :refer [tr]]
   [dre.network :as-alias network]
   [dre.page-util :refer [strip-zerowidth-utf8]]
   [dre.pages.search.events :as search-events]
   [dre.panels.events :as-alias panels]
   [dre.utils :refer [stringify-keys-with-namespaces condensates-role? aborted-request?]]
   [re-frame.core :as rf]
   [superstructor.re-frame.fetch-fx :as-alias fetch-fx]))

(def db-path [:widgets :top-terms])

(def cooc-db-path (conj db-path :cooccurrences))

(def significant-forest-db-path (conj db-path :significant-forest))

(def top-terms-fast-db-path (conj db-path :top-terms-fast))

(def id :top-terms)

(defn cooccurrence-types
  [user]
  (cond->
   [{:id "ALL" :label [tr "All"] :selected-label [tr "Select a category"]}
    {:id "I" :label [tr "Interventions"]}
    {:id "O" :label [tr "Outcomes"]}
    {:id "C" :label [tr "Characteristics"]}
    {:id "THE" :label [tr "Therapeutic Procedure"]}
    {:id "GEN" :label [tr "Gene or Genome"]}
    {:id "DIS" :label [tr "Disease & Disorders"]}
    {:id "INJ" :label [tr "Injury"]}
    {:id "SYM" :label [tr "Symptom"]}
    {:id "CHE" :label [tr "Chemical"]}
    {:id "DRU" :label [tr "Drug"]}
    {:id "VIT" :label [tr "Vitamin"]}
    {:id "ANT" :label [tr "Antibiotic"]}
    {:id "VIR" :label [tr "Virus"]}
    {:id "CEL" :label [tr "Cell"]}
    {:id "ANA" :label [tr "Anatomy"]}
    {:id "MED" :label [tr "Medical Devices"]}
    {:id "BAC" :label [tr "Bacteria"]}
    {:id "SPE" :label [tr "Species"]}
    {:id "DIA" :label [tr "Diagnostic Procedure"]}
    {:id "LAB" :label [tr "Lab Procedure"]}
    {:id "MOL" :label [tr "Biomolecular research"]}
    {:id "PRO" :label [tr "Procedure"]}
    {:id "PHY" :label [tr "Physiology"]}
    {:id "BIO" :label [tr "Biologic Function"]}
    {:id "PHE" :label [tr "Phenomena"]}
    {:id "TST" :label [tr "Lab test result"]}
    {:id "ORG" :label [tr "Medical Organization"]}
    {:id "OCC" :label [tr "Medical Occupation"]}
    {:id "FOD" :label [tr "Food"]}
    {:id "DEM" :label [tr "Demographics"]}]
    (condensates-role? user)
    (conj {:id "CON" :label [tr "Condensates"]})))

(defn- valid-category?
  [user category]
  (contains?
   (->> (cooccurrence-types user)
        (map :id)
        (into #{}))
   (:id category)))

(defn rows-cooc
  [[result filters] [key filter-key] opts]
  (let [f (get filters filter-key)
        k #(get-in % [(keyword key)])
        mf (fn [[idx subjs]]
             (if (== 1 (count subjs))
               [[false true idx (first subjs)]]
               (map vector
                    (repeat true)
                    (cons true (repeat false))
                    (repeat idx)
                    subjs)))
        coll (map-indexed vector (k result))
        collm (into {} coll)
        coll* (mapcat mf coll)
        masters (filter second coll*)
        row (fn [subj drop?]
              (let [phrase (subj/phrase-pills (:phrase subj) opts)
                    pills (if (>  (count phrase) 1)
                            {:pill/pills phrase
                             :pill/type :and}
                            (first phrase))
                    f? (contains? f pills)]
                {:term (:subject-id subj)
                 :class [(when (seq f) (if f? "active" "inactive"))]
                 :open-fn #(rf/dispatch [::panels/set-panel :sense-detail {:modal-params {:phrase phrase}}])
                 :on-click (fn [_] (rf/dispatch [::search-events/widget-filter-select filter-key pills]))
                 :label (str/join ", " ((if drop? rest identity)
                                        (map :label (:phrase subj))))
                 :score (:score subj)}))
        ret (for [[_group? _master? idx subj] masters]
              (row subj false)
              #_(-> (assoc :children (map #(row % true) (rest (get collm idx))))))]
    ret))

(defn rows-significant-forest
  [[result filters] [key filter-key] opts]
  (let [f (get filters filter-key)
        result-key (fn [k]
                     (->> k
                          str/lower-case
                          (str "top-")
                          keyword))
        k (result-key key)
        coll (get-in result [k :sense/forest])
        row-fn
        (fn [{:keys [:label :score :sense]} child?]
          (let [phrase [{:sense sense :label label}]
                phrase-pills (subj/phrase-pills phrase opts)
                pills (first phrase-pills)
                pills (if-not child?
                        (assoc pills :pill/mode "descendants")
                        pills)
                f? (contains? f pills)]
            {:term sense
             :class [(when (seq f) (if f? "active" "inactive"))]
             :open-fn #(rf/dispatch [::panels/set-panel :sense-detail {:modal-params {:phrase phrase}}])
             :on-click (fn [_] (rf/dispatch [::search-events/widget-filter-select filter-key pills]))
             :label label
             :score score}))]
    (distinct-by
     :label
     (for [row coll]
       (let [children (concat
                       (:children row) ;; Massive hack
                       (mapcat :children (:children row)))
             row* (if (= 1 (count children))
                    (first children)
                    row)]
         (-> (row-fn row* false)
             (assoc :children
                    (distinct-by
                     :label
                     (reverse
                      (sort-by (fnil :score 0)
                               (if (> (count children) 1)
                                 (map #(row-fn % true) children)
                                 [])))))))))))

(defn rows-top-terms-fast
  [[result filters] [key filter-key] opts]
  (let [f (get filters filter-key)
        coll (get-in result [:top-by (keyword key)])
        row-fn
        (fn [{:keys [:label :count :sense]}]
          (let [phrase [{:sense sense :label label}]
                phrase-pills (subj/phrase-pills phrase opts)
                pills (first phrase-pills)
                f? (contains? f pills)]
            {:term sense
             :class [(when (seq f) (if f? "active" "inactive"))]
             :open-fn #(rf/dispatch [::panels/set-panel :sense-detail {:modal-params {:phrase phrase}}])
             :on-click (fn [_] (rf/dispatch [::search-events/widget-filter-select filter-key pills]))
             :label label
             :score count}))]
    (distinct-by
     :label
     (for [row coll]
       (row-fn row)))))

(defn data-filters
  [db]
  (->> db
       search-events/db-path
       :widget-filters
       id))

(defn ->csv-string
  [i18n-db data]
  (let [rs (sort-by :score > data)
        labels (mapcat (fn [row]
                         (let [label (strip-zerowidth-utf8 (or (:label row) ""))
                               children (:children row)]
                           (cons (pr-str label #_(:score row))
                                 (for [c children]
                                   (pr-str
                                    (str label " / " (strip-zerowidth-utf8 (or (:label c) "")))
                                    #_(:score c))))))
                       rs)
        header (str (i18n-utils/translate i18n-db "Term"))
        csv (str/join "\n" (cons header labels))]
    csv))

;; -- Events

(rf/reg-event-db
 ::set-status
 (fn
   [db [_ status]]
   (-> db
       #_(assoc-in (conj cooc-db-path :status) status)
       #_(assoc-in (conj significant-forest-db-path :status) status)
       (assoc-in (conj top-terms-fast-db-path :status) status))))

(rf/reg-event-fx
 ::set-loading
 (fn-traced
  [_ _]
  {:dispatch [::set-status :loading]}))

(rf/reg-event-fx
 ::init
 [rf/trim-v]
 (fn
   [{:keys [db]}
    [{:keys [after-fx last-selected-filter-key pills-api-params-fn k custom-category extra]}]]
   (if (= last-selected-filter-key id)
     {:dispatch-n [[::set-status nil]
                   after-fx]}
     (let [user (-> db app-events/db-path :user)
           in-titles-only? (when (get-in db (conj db-path :in-titles-only?))
                             "title")
           #_params #_(-> (pills-api-params-fn [id])
                          (assoc :types (map :id (cooccurrence-types user))
                                 :k (or k 128)
                                 :query/sentence-tag-filter in-titles-only?))
           extra-category (get-in extra [id :child-option])
           default-type "ALL"
           category-selection-id :dre.widgets.top-terms.views/category
           selected-category (get-in db [:widget id category-selection-id] default-type)
           selected-type (or custom-category (:id extra-category) selected-category)]
       {:db (if (valid-category? user {:id selected-type})
              (assoc-in db [widget-events/db-path id category-selection-id] selected-type)
              db)
        :fx [[:dispatch [::set-loading]]
             [:dispatch [::fetch-top-terms-fast {:pills-api-params-fn pills-api-params-fn 
                                                 :k k
                                                 :after-fx after-fx
                                                 :selected-type selected-type
                                                 :in-titles-only? in-titles-only?}]]
             #_[:dispatch [::fetch-significant-forest
                           {:pills-api-params-fn pills-api-params-fn
                            :k k
                            :in-titles-only? in-titles-only?}]]
             #_[::network/request-with-abort
                [{:endpoint :widgets/top-terms}
                 {:body (stringify-keys-with-namespaces params)
                  :on-success [::success {:after-fx after-fx
                                          :pills-api-params-fn pills-api-params-fn
                                          :k k
                                          :attempt attempt}]
                  :on-failure [::failure {:after-fx after-fx}]}]]]}))))

(rf/reg-event-fx
 ::success
 [(rf/path cooc-db-path) rf/trim-v]
 (fn-traced
  [_ [{:keys [after-fx pills-api-params-fn k attempt]
                  ::fetch-fx/keys [response]}]]
  (let [done? (:done? (:body response))
        fx (if (and (not done?) (> 7 attempt))
             [::init {:after-fx after-fx
                      :pills-api-params-fn pills-api-params-fn
                      :k k
                      :attempt (inc attempt)}]
             after-fx)]
    {:db {:status :success
          :data (:top (:body response))
          :done? done?}
     :dispatch fx})))

(rf/reg-event-fx
 ::failure
 [(rf/path cooc-db-path) rf/trim-v]
 (fn-traced
  [_ [{:keys [after-fx]
       ::fetch-fx/keys [response]
       :as opts}]]
  (when-not (aborted-request? response)
    {:db {:status :failure
          :data nil}
     :dispatch-n [after-fx
                  [:dre.network/default-failure (merge {:event :top-terms} opts)]]})))

(rf/reg-event-fx
 ::fetch-significant-forest
 [(rf/path significant-forest-db-path) rf/trim-v]
 (fn
   [{:keys [db]} [{:keys [pills-api-params-fn k in-titles-only?]}]]
   (let [params (-> (pills-api-params-fn [:characteristics :outcomes :interventions])
                    (assoc :algo "mutual-information"
                           :k (or k 256)
                           :query/sentence-tag-filter in-titles-only?))]
     {:db (assoc db :status :loading)
      ::network/request-with-abort [{:endpoint :widgets/pico-significant-forest}
                                    {:body (stringify-keys-with-namespaces params)
                                     :on-success [::success-significant-forest]
                                     :on-failure [::failure-significant-forest]}]})))

(rf/reg-event-db
 ::success-significant-forest
 [(rf/path significant-forest-db-path) rf/trim-v]
 (fn-traced
  [_ [{::fetch-fx/keys [response]}]]
  {:status :success
   :data (:body response)}))

(rf/reg-event-fx
 ::failure-significant-forest
 [(rf/path significant-forest-db-path) rf/trim-v]
 (fn-traced
  [_ [{::fetch-fx/keys [response] :as opts}]]
  (when-not (aborted-request? response)
    {:db {:status :failure
          :data nil}
     :dispatch [:dre.network/default-failure (merge {:event :cooccurrences} opts)]})))

(rf/reg-event-fx
 ::fetch-top-terms-fast
 [(rf/path top-terms-fast-db-path) rf/trim-v]
 (fn
   [{:keys [db]} [{:keys [pills-api-params-fn k in-titles-only? selected-type after-fx]}]]
   (let [params (-> (pills-api-params-fn [id])
                    (assoc :categories [selected-type]
                           :k (or k 500)
                           :query/sentence-tag-filter in-titles-only?))]
     {:db (assoc db :status :loading)
      ::network/request-with-abort [{:endpoint :widgets/top-terms-fast}
                                    {:body (stringify-keys-with-namespaces params)
                                     :on-success [::success-top-terms-fast {:after-fx after-fx}]
                                     :on-failure [::failure-top-terms-fast {:after-fx after-fx}]}]})))

(rf/reg-event-fx
  ::success-top-terms-fast
 [(rf/path top-terms-fast-db-path) rf/trim-v]
 (fn-traced
  [{db :db} [{:keys [after-fx ::fetch-fx/response]}]]
  {:db {:status :success
        :data (:body response)}
   :dispatch after-fx}))

(rf/reg-event-fx
 ::failure-top-terms-fast
 [(rf/path top-terms-fast-db-path) rf/trim-v]
 (fn-traced
  [_ [{:keys [after-fx ::fetch-fx/response]}]]
  (when-not (aborted-request? response)
    {:db {:status :failure
          :data nil}
     :dispatch-n [after-fx
                  [:dre.network/default-failure (merge {:event :cooccurrences} response)]]})))

;; todo
(rf/reg-event-fx
 ::export
 [rf/trim-v]
 (fn-traced
  [{:keys [db]} [key export-count]]
  (let [opts (when (get-in db (conj db-path :in-titles-only?))
               {:query/sentence-tag-filter "title"})
        rows (->> (rows-top-terms-fast [(get-in db (conj top-terms-fast-db-path :data)) nil] [key id] opts)
                 (take (js/parseInt (name export-count))))
        #_rows #_(if (contains? #{"I" "O" "C"} key) ; Use rows-significant-forest  for Intervention, Outcome and Characteristic data when exporting
               (rows-significant-forest [(get-in db (conj significant-forest-db-path :data)) nil] [key id] opts)
               (rows-cooc [(get-in db (conj cooc-db-path :data)) nil] [key id] opts)) 
        csv-string (->csv-string (get db :i18n) rows)]
    {:dispatch [::dre.fx/download {:content csv-string
                                   :filename (str (name id) ".csv")
                                   :mimetype "text/csv;charset=UTF-8"}]})))

(rf/reg-event-fx
 ::toggle-title-filter
 (fn-traced
  [{:keys [db]} _]
  (let [search-db (get db search-events/db-path)
        search-query (:search-query search-db)
        last-selected-filter-key (:last-selected-filter-key search-db)
        widget-filters (:widget-filters search-db)
        aq (partial search-events/analysis-query search-query widget-filters)
        aq* (comp second aq)]
    {:db (update-in db (conj db-path :in-titles-only?) not)
     :dispatch [::init {:pills-api-params-fn aq*
                        :search-query search-query
                        :last-selected-filter-key last-selected-filter-key}]})))

(rf/reg-event-fx
 ::trigger-fetch-top-terms-fast
 (fn-traced
  [{:keys [db]} _ [category]]
  (let [search-db (get db search-events/db-path)
        search-query (:search-query search-db)
        last-selected-filter-key (:last-selected-filter-key search-db)
        widget-filters (:widget-filters search-db)
        aq (partial search-events/analysis-query search-query widget-filters)
        aq* (comp second aq)]
    {:dispatch [::init {:pills-api-params-fn aq*
                        :search-query search-query
                        :custom-category category
                        :last-selected-filter-key last-selected-filter-key}]})))

;; -- Subscriptions

(rf/reg-sub
 ::state
 (fn-traced
  [db _]
  (get-in db db-path)))

(rf/reg-sub
 ::cooc-state
 (fn-traced
  [db _]
  (get-in db cooc-db-path)))

(rf/reg-sub
 ::significant-forest-state
 (fn-traced
  [db _]
  (get-in db significant-forest-db-path)))

(rf/reg-sub
 ::top-terms-fast-state
 (fn-traced
  [db _]
  (get-in db top-terms-fast-db-path)))

(rf/reg-sub
 ::loading?
 :<- [::cooc-state]
 :<- [::significant-forest-state]
 :<- [::top-terms-fast-state]
 (fn-traced
  [[cooc-state significant-forest-state top-terms-fast-state] _]
  (or
   #_(= :loading (:status cooc-state))
   #_(= :loading (:status significant-forest-state))
   (= :loading (:status top-terms-fast-state)))))

(rf/reg-sub
 ::done?
 :<- [::cooc-state]
 (fn-traced
  [state _]
  (:done? state)))

(rf/reg-sub
 ::data
 :<- [::cooc-state]
 :<- [::significant-forest-state]
 :<- [::search-events/widget-filters]
 :<- [::in-titles-only?]
 :<- [::top-terms-fast-state]
 (fn-traced
  [[cooc-state significant-forest-state filters in-titles-only? top-terms-fast-state] [_ key]]
  (let [opts (when in-titles-only?
               {:query/sentence-tag-filter "title"})]
    (rows-top-terms-fast [(:data top-terms-fast-state) filters] [key id] opts)
    #_(if (contains? #{"I" "O" "C"} key)
      (rows-significant-forest [(:data significant-forest-state) filters] [key id] opts)
      (rows-cooc [(:data cooc-state) filters] [key id] opts)))))

(rf/reg-sub
 ::used-in-search?
 (fn-traced
  [db _]
  (let [x (data-filters db)]
    (not-empty x))))

(rf/reg-sub
 ::in-titles-only?
 :<- [::state]
 (fn-traced
  [state]
  (:in-titles-only? state)))
